import {
  buildWorkEpisodeActionHookFragment,
  buildWorkEpisodeIntentHookFragment,
  buildWorkEpisodeProjectionHookFragment,
} from "./session-work-episode-hook-fragment.js";
import { buildSessionSummaryCollectorHookFragment } from "./session-summary-collector-hook-fragment.js";
import {
  buildCredentialFreeHttpsUriHookFragment,
  SESSION_SUMMARY_MAX_NAMED_TOOL_KEYS,
} from "./session-summary-contract.js";

/**
 * Lean session-summary hook.
 *
 * Replaces the "append every event forever" spool (which reached 148MB /
 * 231,575 lines / 3,606 sessions on a real machine and never uploaded a byte)
 * with a bounded per-session counter plus compact summary snapshots written to
 * a durable local queue at session boundaries.
 *
 *   SessionStart / UserPromptSubmit / PreToolUse / PostToolUse
 *   / PostToolUseFailure / PermissionRequest
 *     -> one bounded read-modify-write of a small per-session JSON file.
 *        Never a network call, never an unbounded append.
 *   Stop / RunEnd / SessionEnd
 *     -> enqueue ONE compact boundary summary. RunEnd resets only the run
 *        projection; SessionEnd emits and clears the cumulative conversation.
 *        A separate CLI delivery worker owns networking.
 *
 * The generated script is the single source of truth: `session-summary-hook.test.ts`
 * imports the emitted `.mjs` and drives its exported `main()`, so the artifact
 * that actually runs on the user's machine is the artifact under test.
 *
 * Generated-script style matches `buildExecutionGraphEmitScriptContent`: no
 * backticks, no backslash escapes, string concatenation only, so the source
 * survives this template literal verbatim.
 */

export const SESSION_SUMMARY_HOOK_MARKER = "orgx-session-summary.mjs";

/**
 * Canonical server paths for summary delivery and the rollout fallback.
 *
 * The canonical endpoint is workspace-scoped and initiative-optional. A
 * 404/405 still falls back to the durable Work Graph report sink, preserving a
 * summary during a partial rollout without treating a server error as success.
 *
 */
export const SESSION_SUMMARY_ENDPOINT_PATH = "/api/v1/sessions/summary";
export const SESSION_SUMMARY_FALLBACK_ENDPOINT_PATH = "/api/v1/work-graph/reports";

export const SESSION_SUMMARY_SCHEMA_VERSION = "1.0.0";

/** Doctor marker for hooks that can inject bounded first-turn native context. */
export const SESSION_SUMMARY_FIRST_TURN_CONTEXT_MARKER =
  "orgx-native-session-start-context/v1";

/** Terminal hook events that flush a session summary. */
export const SESSION_SUMMARY_TERMINAL_EVENTS = ["Stop", "RunEnd", "SessionEnd"] as const;

/**
 * Cap for the legacy raw spool (`events.jsonl`). The spool stays installed
 * because `orgx-wizard hooks replay` and `hooks doctor` read it, but replay only
 * ever consumes the recent tail (default 200 records), so a bounded tail loses
 * nothing those surfaces use. At the cap the file is rotated to a single `.1`
 * generation, bounding total spool footprint at 2x this value instead of the
 * unbounded growth that produced 148MB.
 */
export const HOOK_OUTBOX_MAX_BYTES = 8 * 1024 * 1024;

/**
 * The installed session-summary hook script.
 *
 * Every failure path is swallowed and the process still exits 0: a hook must
 * never block or slow the user's session.
 */
export function buildSessionSummaryHookScriptContent(): string {
  return `#!/usr/bin/env node
// OrgX lean session-summary hook. Generated by @useorgx/wizard.
//
// Hot path (PostToolUse and friends): ONE small read-modify-write. No network.
// Terminal (Stop / SessionEnd): ONE compact cumulative summary queue write.
// A separate orgx-wizard hooks flush process owns all network delivery.
//
// NOTE ON Stop SEMANTICS: in Claude Code, Stop fires at every assistant turn
// boundary, not only once at session end. Stop therefore writes a cumulative
// snapshot and retains state. The ingest endpoint merges repeated snapshots by
// session_id; SessionEnd, when the client sends it, queues the final snapshot
// and clears local state.

import { spawn } from "node:child_process";
import { createHash } from "node:crypto";
import { closeSync, fsyncSync, mkdirSync, openSync, readFileSync, readdirSync, realpathSync, rmdirSync, writeSync, rmSync, renameSync, statSync } from "node:fs";
import { homedir } from "node:os";
import { join, dirname, basename, resolve } from "node:path";
import { fileURLToPath } from "node:url";

var SCHEMA_VERSION = "${SESSION_SUMMARY_SCHEMA_VERSION}";
var NATIVE_SESSION_START_CONTEXT_VERSION = "${SESSION_SUMMARY_FIRST_TURN_CONTEXT_MARKER}";
var MAX_NATIVE_SESSION_START_CONTEXT_BYTES = 5 * 1024;

export function parseArgs(argv) {
  var args = {};
  for (var i = 0; i < argv.length; i++) {
    var arg = argv[i];
    if (arg.indexOf("--") !== 0) continue;
    var eq = arg.indexOf("=");
    if (eq < 0) args[arg.slice(2)] = "true";
    else args[arg.slice(2, eq)] = arg.slice(eq + 1);
  }
  return args;
}

export function pickString() {
  for (var i = 0; i < arguments.length; i++) {
    var value = arguments[i];
    if (typeof value !== "string") continue;
    var trimmed = value.trim();
    if (trimmed) return trimmed;
  }
  return undefined;
}

function configHome() {
  var xdg = pickString(process.env.XDG_CONFIG_HOME);
  return xdg ? xdg : join(homedir(), ".config");
}

function defaultStateDir() {
  var configured = pickString(process.env.ORGX_WIZARD_CONFIG_HOME);
  var root = configured ? configured : join(configHome(), "useorgx", "wizard");
  return join(root, "sessions");
}

function defaultQueueDir() {
  var configured = pickString(process.env.ORGX_WIZARD_CONFIG_HOME);
  var root = configured ? configured : join(configHome(), "useorgx", "wizard");
  return join(root, "session-summary-captures");
}

function workContextRoot(env) {
  var configured = pickString(env.ORGX_WIZARD_CONFIG_HOME);
  var xdg = pickString(env.XDG_CONFIG_HOME);
  return configured
    ? configured
    : join(xdg ? xdg : join(homedir(), ".config"), "useorgx", "wizard");
}

function defaultWorkContextPath(env, cwd, sourceClient, sessionId) {
  if (!cwd) return null;
  var canonicalCwd = canonicalWorkCwd(cwd);
  var identity = sourceClient && sessionId
    ? JSON.stringify([canonicalCwd, sourceClient, sessionId])
    : canonicalCwd;
  var key = createHash("sha256").update(identity).digest("hex");
  return join(workContextRoot(env), "session-work-contexts", key + ".json");
}

function canonicalWorkCwd(cwd) {
  var absolute = resolve(cwd);
  var ancestor = absolute;
  var missing = [];

  while (true) {
    try {
      return resolve(realpathSync.native(ancestor), ...missing);
    } catch (e) {
      var parent = dirname(ancestor);
      if (parent === ancestor) return absolute;
      missing.unshift(basename(ancestor));
      ancestor = parent;
    }
  }
}

function validNativeSourceClient(value) {
  return typeof value === "string" && value.length > 0 && value.length <= 120;
}

function claimableNativeSourceClient(value) {
  return value === "claude-code" || value === "codex" || value === "cursor" || value === "opencode";
}

function validNativeSessionId(value) {
  return typeof value === "string" && value.length > 0 && value.length <= 500;
}

function truncateWorkUtf8(value, maxBytes) {
  if (Buffer.byteLength(value, "utf8") <= maxBytes) return value;
  var suffix = String.fromCharCode(10) + "[OrgX work context truncated at its bounded hook limit.]";
  var budget = Math.max(0, maxBytes - Buffer.byteLength(suffix, "utf8"));
  var output = "";
  for (var character of value) {
    if (Buffer.byteLength(output + character, "utf8") > budget) break;
    output += character;
  }
  return output + suffix;
}

export function buildNativeSessionStartHookOutput(context) {
  if (!context) return null;
  var additionalContext = truncateWorkUtf8(
    [
      "OrgX session work context (producer-asserted; references retain their own provenance).",
      "This activation is bound to the exact working directory and native session. Reconfirm consequential state if a reference is stale.",
      JSON.stringify(context),
    ].join(String.fromCharCode(10)),
    MAX_NATIVE_SESSION_START_CONTEXT_BYTES
  );
  return {
    continue: true,
    suppressOutput: true,
    hookSpecificOutput: {
      hookEventName: "SessionStart",
      additionalContext: additionalContext,
    },
  };
}

// Mirrors the backfill distiller so live and historical records agree on repo.
export function repoOf(cwd) {
  if (!cwd) return "unknown";
  var marker = "/Code/";
  var at = cwd.indexOf(marker);
  if (at >= 0) {
    var rest = cwd.slice(at + marker.length);
    var slash = rest.indexOf("/");
    var name = slash < 0 ? rest : rest.slice(0, slash);
    if (name) return name;
  }
  return basename(cwd) || "unknown";
}

function safeFilePart(value) {
  return String(value).replace(/[^A-Za-z0-9._-]/g, "_").slice(0, 128);
}

export function enqueueSummaryCapture(queueDir, summary, initiativeId, nowIso, captureKind) {
  var root = queueDir ? queueDir : defaultQueueDir();
  var stamp = String(nowIso).replace(/[^A-Za-z0-9]/g, "_");
  var episode = summary.metadata && summary.metadata.work_episode;
  var episodeId = episode && episode.episode_id ? episode.episode_id : summary.session_id;
  var captureId = [summary.source_client, episodeId, captureKind, summary.ended_at, summary.events].join(":");
  var nonce = Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
  var name = "summary-" + safeFilePart(summary.source_client) + "-" + safeFilePart(summary.session_id) + "-" + stamp + "-" + process.pid + "-" + nonce + ".capture.json";
  var target = join(root, name);
  var temporary = target + ".tmp";
  var candidate = {
    schema_version: "orgx-session-summary-capture/v1",
    capture_id: captureId,
    capture_kind: captureKind,
    queued_at: nowIso,
    session: summary,
  };
  if (initiativeId) candidate.initiative_id = initiativeId;
  var capture = compactSummaryCapture(candidate, MAX_SESSION_SUMMARY_BODY_BYTES);
  if (!capture) return null;
  try {
    mkdirSync(root, { recursive: true, mode: 0o700 });
    var descriptor = openSync(temporary, "w", 0o600);
    try {
      writeSync(descriptor, JSON.stringify(capture), undefined, "utf8");
      fsyncSync(descriptor);
    } finally {
      closeSync(descriptor);
    }
    renameSync(temporary, target);
    return { path: target, capture: capture };
  } catch (e) {
    try { rmSync(temporary, { force: true }); } catch (ignored) {}
    return null;
  }
}

export function emptyState(sessionId, sourceClient, cwd, nowIso, stateScope, runOrdinal) {
  return {
    session_id: sessionId,
    source_client: sourceClient,
    cwd: cwd === undefined ? null : cwd,
    repo: repoOf(cwd),
    first_ts: nowIso,
    last_ts: nowIso,
    events: 0,
    prompts: 0,
    tool_calls: 0,
    tools: {},
    edits: 0,
    bash: 0,
    action_seq: 0,
    actions: [],
    actions_omitted: 0,
    completed_action_aggregate: emptyCompletedActionAggregate(0),
    pending_actions: [],
    pending_starts_omitted: 0,
    permission_requests: 0,
    permission_modes: [],
    intent_seq: 0,
    intent_observations: [],
    intent_observations_omitted: 0,
    lineage: null,
    work_context: null,
    work_context_locked: false,
    state_scope: stateScope === "run" ? "run" : "conversation",
    ...(stateScope === "run" && Number.isInteger(runOrdinal)
      ? { run_ordinal: runOrdinal }
      : {}),
  };
}

${buildSessionSummaryCollectorHookFragment()}

// One bounded read-modify-write. No spool, no network, no unbounded growth:
// the tool map is capped so a pathological session cannot inflate the file.
var MAX_TOOL_KEYS = 40;
var MAX_CAPTURE_ACTIONS = 32;
var MAX_PENDING_ACTIONS = 32;
var MAX_INTENT_OBSERVATIONS = 12;
var MAX_INTENT_EXCERPT = 600;
var COMPLETED_ACTION_AGGREGATE_VERSION = "orgx-session-completed-action-aggregate/v1";
var COMPLETED_ACTION_DIGEST_ALGORITHM = "sha256-chain-v1";
var EMPTY_COMPLETED_ACTION_DIGEST = createHash("sha256")
  .update(COMPLETED_ACTION_AGGREGATE_VERSION + ":empty")
  .digest("hex");
var COMPLETED_ACTION_STATUSES = ["succeeded", "failed", "completed_unknown"];
var COMPLETED_ACTION_EFFECTS = ["inspect", "change", "execute", "network", "invoke", "unspecified"];

function zeroCounts(keys) {
  var counts = {};
  for (var i = 0; i < keys.length; i++) counts[keys[i]] = 0;
  return counts;
}

function emptyCompletedActionAggregate(unaggregatedActions) {
  return {
    schema_version: COMPLETED_ACTION_AGGREGATE_VERSION,
    aggregated_actions: 0,
    unaggregated_actions: Number.isInteger(unaggregatedActions) && unaggregatedActions > 0
      ? unaggregatedActions
      : 0,
    status_counts: zeroCounts(COMPLETED_ACTION_STATUSES),
    effect_counts: zeroCounts(COMPLETED_ACTION_EFFECTS),
    first_completed_at: null,
    last_completed_at: null,
    digest_sha256: EMPTY_COMPLETED_ACTION_DIGEST,
  };
}

function isPromptEvent(event) {
  return event === "UserPromptSubmit" || String(event).indexOf("user_prompt") >= 0;
}

function isToolCompletionEvent(event) {
  var normalized = String(event).toLowerCase();
  return event === "PostToolUse" ||
    event === "PostToolUseFailure" ||
    normalized.indexOf("post_tool_use") >= 0;
}

function ensureExecutionState(state) {
  if (!Number.isFinite(state.action_seq)) state.action_seq = 0;
  if (!Array.isArray(state.actions)) state.actions = [];
  if (!Number.isFinite(state.actions_omitted)) state.actions_omitted = 0;
  if (
    !state.completed_action_aggregate ||
    typeof state.completed_action_aggregate !== "object" ||
    state.completed_action_aggregate.schema_version !== COMPLETED_ACTION_AGGREGATE_VERSION ||
    !Number.isInteger(state.completed_action_aggregate.aggregated_actions) ||
    state.completed_action_aggregate.aggregated_actions < 0 ||
    !Number.isInteger(state.completed_action_aggregate.unaggregated_actions) ||
    state.completed_action_aggregate.unaggregated_actions < 0 ||
    !state.completed_action_aggregate.status_counts ||
    !state.completed_action_aggregate.effect_counts ||
    typeof state.completed_action_aggregate.digest_sha256 !== "string" ||
    !/^[0-9a-f]{64}$/.test(state.completed_action_aggregate.digest_sha256)
  ) {
    // A pre-aggregate state can continue safely, but its earlier completions
    // remain explicit and prevent this session from claiming aggregate
    // completeness. New sessions start with zero unaggregated actions.
    state.completed_action_aggregate = emptyCompletedActionAggregate(
      Number.isInteger(state.tool_calls) && state.tool_calls > 0 ? state.tool_calls : 0
    );
  }
  if (!Array.isArray(state.pending_actions)) state.pending_actions = [];
  if (!Number.isFinite(state.pending_starts_omitted)) state.pending_starts_omitted = 0;
  if (!Number.isFinite(state.permission_requests)) state.permission_requests = 0;
  if (!Array.isArray(state.permission_modes)) state.permission_modes = [];
  if (!Number.isFinite(state.intent_seq)) state.intent_seq = 0;
  if (!Array.isArray(state.intent_observations)) state.intent_observations = [];
  if (!Number.isFinite(state.intent_observations_omitted)) state.intent_observations_omitted = 0;
  return state;
}

${buildWorkEpisodeIntentHookFragment()}
function nextActionId(state) {
  state.action_seq += 1;
  return "action-" + String(state.action_seq).padStart(6, "0");
}

function boundedString(value, max) {
  var text = pickString(value);
  return text ? text.slice(0, max) : undefined;
}

function safeDuration(value) {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return undefined;
  return Math.min(Math.round(value), 86400000);
}

function actionResult(event, sourceClient) {
  if (event === "PostToolUseFailure" || String(event).toLowerCase().indexOf("failure") >= 0) {
    return "failed";
  }
  // Claude's PostToolUse contract fires only after success. Codex documents
  // PostToolUse for completed tools, including non-zero Bash exits, so its
  // outcome must remain explicitly unclassified without reading tool output.
  return sourceClient === "claude-code" || sourceClient === "cursor" || sourceClient === "opencode"
    ? "succeeded"
    : "completed_unknown";
}

function completedActionEffectBucket(effect) {
  return COMPLETED_ACTION_EFFECTS.indexOf(effect) >= 0 && effect !== "unspecified"
    ? effect
    : "unspecified";
}

function completedActionDigestMaterial(action) {
  return [
    "orgx-session-completed-action/v1",
    action.id || null,
    action.tool_name || null,
    action.status || null,
    action.started_at || null,
    action.completed_at || null,
    action.duration_ms === undefined ? null : action.duration_ms,
    action.turn_id || null,
    action.effect || null,
    action.target || null,
  ];
}

function observeCompletedActionAggregate(state, action) {
  var aggregate = state.completed_action_aggregate;
  aggregate.aggregated_actions += 1;
  var status = COMPLETED_ACTION_STATUSES.indexOf(action.status) >= 0
    ? action.status
    : "completed_unknown";
  aggregate.status_counts[status] = (aggregate.status_counts[status] || 0) + 1;
  var effect = completedActionEffectBucket(action.effect);
  aggregate.effect_counts[effect] = (aggregate.effect_counts[effect] || 0) + 1;
  if (!aggregate.first_completed_at) aggregate.first_completed_at = action.completed_at;
  aggregate.last_completed_at = action.completed_at;
  aggregate.digest_sha256 = createHash("sha256")
    .update(
      COMPLETED_ACTION_AGGREGATE_VERSION + String.fromCharCode(10) +
      aggregate.digest_sha256 + String.fromCharCode(10) +
      JSON.stringify(completedActionDigestMaterial(action))
    )
    .digest("hex");
}

function pendingIndex(state, toolUseId) {
  if (!toolUseId) return -1;
  for (var i = 0; i < state.pending_actions.length; i++) {
    if (state.pending_actions[i].correlation_key === toolUseId) return i;
  }
  return -1;
}

${buildWorkEpisodeActionHookFragment()}
function observeAction(state, input) {
  var event = input.event;
  var toolName = boundedString(input.toolName, 120);
  if (!toolName) return;
  var toolUseId = boundedString(input.toolUseId, 200);

  if (event === "PreToolUse" || String(event).toLowerCase().indexOf("pre_tool_use") >= 0) {
    if (pendingIndex(state, toolUseId) >= 0) return;
    if (state.pending_actions.length >= MAX_PENDING_ACTIONS) {
      state.pending_starts_omitted += 1;
      return;
    }
    state.pending_actions.push({
      id: nextActionId(state),
      correlation_key: toolUseId,
      tool_name: normalizeToolName(toolName),
      started_at: input.nowIso,
      turn_id: boundedString(input.turnId, 120),
      ...(input.actionEffect ? { effect: input.actionEffect } : {}),
      ...(input.actionTarget ? { target: input.actionTarget } : {}),
    });
    return;
  }

  if (!isToolCompletionEvent(event)) return;
  var index = pendingIndex(state, toolUseId);
  var pending = index >= 0 ? state.pending_actions.splice(index, 1)[0] : null;
  if (!pending) state.pending_starts_omitted += 1;
  var action = {
    id: pending ? pending.id : nextActionId(state),
    type: "tool.invoke",
    tool_name: normalizeToolName(toolName),
    status: actionResult(event, state.source_client),
    started_at: pending ? pending.started_at : undefined,
    completed_at: input.nowIso,
    duration_ms: safeDuration(input.durationMs),
    turn_id: boundedString(input.turnId, 120) || (pending ? pending.turn_id : undefined),
    provenance: "runtime_observed",
    ...(input.actionEffect || (pending ? pending.effect : undefined)
      ? { effect: input.actionEffect || pending.effect }
      : {}),
    ...(input.actionTarget || (pending ? pending.target : undefined)
      ? { target: input.actionTarget || pending.target }
      : {}),
  };
  observeCompletedActionAggregate(state, action);
  if (state.actions.length < MAX_CAPTURE_ACTIONS) state.actions.push(action);
  else {
    state.actions.splice(8, 1);
    state.actions.push(action);
    state.actions_omitted += 1;
  }
}

export function applyEvent(state, input) {
  ensureExecutionState(state);
  var event = input.event;
  var toolName = input.toolName;
  state.events += 1;
  state.last_ts = input.nowIso;

  if (isPromptEvent(event)) state.prompts += 1;
  observeIntent(state, input);
  observeLineage(state, input);

  if (event === "PermissionRequest") state.permission_requests += 1;
  var permissionMode = boundedString(input.permissionMode, 40);
  if (
    permissionMode &&
    state.permission_modes.indexOf(permissionMode) < 0 &&
    state.permission_modes.length < 8
  ) {
    state.permission_modes.push(permissionMode);
  }

  observeAction(state, input);

  if (toolName && isToolCompletionEvent(event)) {
    state.tool_calls += 1;
    var known = state.tools[toolName] !== undefined;
    if (known || Object.keys(state.tools).length < MAX_TOOL_KEYS) {
      state.tools[toolName] = (state.tools[toolName] || 0) + 1;
    }
    if (toolName === "Write" || toolName === "Edit" || toolName === "NotebookEdit") state.edits += 1;
    if (toolName === "Bash") state.bash += 1;
  }
  return state;
}

${buildWorkEpisodeProjectionHookFragment()}
// Long MCP tool names ("mcp__<uuid>__<tool>") can make a raw tool map dominate
// the payload, so keep the heaviest tools and bucket the tail. This bounds the
// summary regardless of how varied or long a session gets.
var MAX_REPORTED_TOOLS = ${SESSION_SUMMARY_MAX_NAMED_TOOL_KEYS};
var MAX_TOOL_NAME = 48;

// "mcp__c2ed4428-8c64-4586-9c2a-52eac05637b8__search-contacts" is mostly an
// opaque server uuid. The tool suffix is the part with analytic value, so
// collapse the uuid rather than truncating the informative end.
export function normalizeToolName(name) {
  var mcp = /^mcp__[0-9a-fA-F-]{16,}__(.+)$/.exec(name);
  var base = mcp ? "mcp__" + mcp[1] : name;
  return base.length > MAX_TOOL_NAME ? base.slice(0, MAX_TOOL_NAME) : base;
}

export function boundTools(tools) {
  // Collapse first so two variants of the same tool merge before ranking.
  var merged = {};
  var names = Object.keys(tools);
  for (var i = 0; i < names.length; i++) {
    var key = normalizeToolName(names[i]);
    merged[key] = (merged[key] || 0) + tools[names[i]];
  }
  var entries = Object.keys(merged).map(function (k) { return [k, merged[k]]; });
  entries.sort(function (a, b) { return b[1] - a[1]; });
  var out = {};
  if (entries.length <= MAX_REPORTED_TOOLS) {
    for (var j = 0; j < entries.length; j++) out[entries[j][0]] = entries[j][1];
    return out;
  }
  for (var k = 0; k < MAX_REPORTED_TOOLS; k++) out[entries[k][0]] = entries[k][1];
  var other = 0;
  for (var m = MAX_REPORTED_TOOLS; m < entries.length; m++) other += entries[m][1];
  out.__other__ = other;
  out.__other_tools__ = entries.length - MAX_REPORTED_TOOLS;
  return out;
}

function buildExecutionObservation(state, captureKind) {
  ensureExecutionState(state);
  var remaining = Math.max(0, MAX_CAPTURE_ACTIONS - state.actions.length);
  var includedPending = state.pending_actions.slice(0, remaining).map(function (pending) {
    return {
      id: pending.id,
      type: "tool.invoke",
      tool_name: pending.tool_name,
      status: "running",
      started_at: pending.started_at,
      turn_id: pending.turn_id,
      provenance: "runtime_observed",
      ...(pending.effect ? { effect: pending.effect } : {}),
      ...(pending.target ? { target: pending.target } : {}),
    };
  });
  var pendingOmitted = Math.max(0, state.pending_actions.length - includedPending.length);
  var actions = state.actions.concat(includedPending).map(function (action) {
    var out = {
      id: boundedString(action.id, 80),
      type: "tool.invoke",
      tool_name: boundedString(action.tool_name, 48),
      status: action.status,
      provenance: "runtime_observed",
    };
    if (action.started_at) out.started_at = action.started_at;
    if (action.completed_at) out.completed_at = action.completed_at;
    if (action.duration_ms !== undefined) out.duration_ms = action.duration_ms;
    if (action.turn_id) out.turn_id = action.turn_id;
    if (action.effect) out.effect = boundedString(action.effect, 40);
    if (action.target) out.target = boundedString(action.target, 240);
    return out;
  });
  var omitted = state.actions_omitted + pendingOmitted;
  var aggregateState = state.completed_action_aggregate;
  var completedDetailsIncluded = state.actions.length;
  var completedDetailsOmitted = state.actions_omitted;
  var observedCompleted = state.tool_calls;
  var aggregatedCompleted = aggregateState.aggregated_actions;
  var unaggregatedCompleted = aggregateState.unaggregated_actions;
  var statusCountTotal = COMPLETED_ACTION_STATUSES.reduce(function (sum, status) {
    return sum + (Number(aggregateState.status_counts[status]) || 0);
  }, 0);
  var effectCountTotal = COMPLETED_ACTION_EFFECTS.reduce(function (sum, effect) {
    return sum + (Number(aggregateState.effect_counts[effect]) || 0);
  }, 0);
  var aggregateMatchesObserved = aggregatedCompleted + unaggregatedCompleted === observedCompleted;
  var detailsMatchObserved = completedDetailsIncluded + completedDetailsOmitted === observedCompleted;
  var statusCountsMatchAggregate = statusCountTotal === aggregatedCompleted;
  var effectCountsMatchAggregate = effectCountTotal === aggregatedCompleted;
  var aggregateCaptureComplete =
    unaggregatedCompleted === 0 &&
    aggregateMatchesObserved &&
    detailsMatchObserved &&
    statusCountsMatchAggregate &&
    effectCountsMatchAggregate;
  var completedActionAggregate = {
    schema_version: COMPLETED_ACTION_AGGREGATE_VERSION,
    capture_complete: aggregateCaptureComplete,
    actions_completed_observed: observedCompleted,
    actions_aggregated: aggregatedCompleted,
    actions_unaggregated: unaggregatedCompleted,
    details_included: completedDetailsIncluded,
    details_omitted: completedDetailsOmitted,
    detail_mode: completedDetailsOmitted > 0 ? "representative" : "complete",
    detail_strategy: completedDetailsOmitted > 0 ? "first_8_last_24" : "all",
    detail_limit: MAX_CAPTURE_ACTIONS,
    status_counts: COMPLETED_ACTION_STATUSES.reduce(function (out, status) {
      out[status] = Number(aggregateState.status_counts[status]) || 0;
      return out;
    }, {}),
    effect_counts: COMPLETED_ACTION_EFFECTS.reduce(function (out, effect) {
      out[effect] = Number(aggregateState.effect_counts[effect]) || 0;
      return out;
    }, {}),
    ...(aggregateState.first_completed_at
      ? { first_completed_at: aggregateState.first_completed_at }
      : {}),
    ...(aggregateState.last_completed_at
      ? { last_completed_at: aggregateState.last_completed_at }
      : {}),
    digest: {
      algorithm: COMPLETED_ACTION_DIGEST_ALGORITHM,
      value: "sha256:" + aggregateState.digest_sha256,
    },
    consistency: {
      aggregate_count_matches_observed: aggregateMatchesObserved,
      detail_partition_matches_observed: detailsMatchObserved,
      status_counts_match_aggregate: statusCountsMatchAggregate,
      effect_counts_match_aggregate: effectCountsMatchAggregate,
    },
  };
  var workContext = state.work_context && typeof state.work_context === "object"
    ? state.work_context
    : null;
  var unavailable = ["verification"];
  if (!workContext || !workContext.intent) unavailable.push("intent");
  if (!workContext || !workContext.authority) unavailable.push("authority_decision");
  if (!workContext || !workContext.cost) unavailable.push("cost");
  if (!workContext || !Array.isArray(workContext.artifact_refs) || workContext.artifact_refs.length === 0) {
    unavailable.push("artifact_refs");
  }
  return {
    schema_version: "orgx-session-execution-observation/v1",
    boundary: captureKind || "legacy_unspecified",
    terminal_observed: captureKind === "run_end" || captureKind === "session_end",
    actions: actions,
    completed_action_aggregate: completedActionAggregate,
    actions_completed_observed: state.tool_calls,
    actions_omitted: omitted,
    start_times_omitted: state.pending_starts_omitted,
    pending_actions: state.pending_actions.length,
    permission_requests_observed: state.permission_requests,
    permission_modes_observed: state.permission_modes.slice(0, 8),
    action_capture_complete:
      aggregateCaptureComplete &&
      state.pending_starts_omitted === 0 &&
      state.pending_actions.length === 0,
    unavailable_fields: unavailable,
  };
}

export function buildSummary(state, nowIso, captureKind) {
  var first = Date.parse(state.first_ts);
  var last = Date.parse(state.last_ts || nowIso);
  var spanOk = isFinite(first) && isFinite(last);
  var metadata = { execution: buildExecutionObservation(state, captureKind) };
  if (state.work_context && typeof state.work_context === "object") {
    metadata.work_context = state.work_context;
  }
  metadata.work_episode = buildWorkEpisode(state, captureKind);
  return {
    schema_version: SCHEMA_VERSION,
    source: "orgx_session_summary",
    session_id: state.session_id,
    source_client: state.source_client,
    repo: state.repo,
    cwd: state.cwd,
    day: isFinite(first) ? new Date(first).toISOString().slice(0, 10) : "unknown",
    started_at: state.first_ts,
    ended_at: state.last_ts,
    duration_min: spanOk ? Math.round((last - first) / 60000) : 0,
    events: state.events,
    prompts: state.prompts,
    tool_calls: state.tool_calls,
    tools: boundTools(state.tools),
    edits: state.edits,
    bash: state.bash,
    metadata: metadata,
  };
}

async function readStdin() {
  try {
    var chunks = [];
    for await (var chunk of process.stdin) chunks.push(Buffer.from(chunk));
    return Buffer.concat(chunks).toString("utf8");
  } catch (e) {
    return "";
  }
}

export function parseJson(value) {
  try {
    var parsed = JSON.parse(value || "{}");
    var ok = parsed && typeof parsed === "object" && !Array.isArray(parsed);
    return ok ? parsed : {};
  } catch (e) {
    return {};
  }
}

function workString(value, max) {
  if (typeof value !== "string") return null;
  var trimmed = value.trim();
  return trimmed && trimmed.length <= max ? trimmed : null;
}

function workObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : null;
}

function hasOnlyKeys(value, allowed) {
  var object = workObject(value);
  if (!object) return false;
  return Object.keys(object).every(function (key) { return allowed.indexOf(key) >= 0; });
}

function hasKeys(value, required) {
  var object = workObject(value);
  if (!object) return false;
  return required.every(function (key) {
    return Object.prototype.hasOwnProperty.call(object, key);
  });
}

function isExactWorkRefShape(value) {
  return hasKeys(value, ["system", "type", "id"])
    && hasOnlyKeys(value, ["system", "type", "id", "uri", "version"]);
}

function isExactWorkRefListShape(value) {
  return Array.isArray(value) && value.every(isExactWorkRefShape);
}

function isExactWorkContextShape(value) {
  if (!hasKeys(value, ["schema_version", "provenance", "intent", "authority", "cost", "artifact_refs", "evidence_refs"])) return false;
  if (!hasOnlyKeys(value, ["schema_version", "provenance", "intent", "authority", "cost", "artifact_refs", "evidence_refs", "semantic_refs"])) return false;
  if (value.provenance !== "producer_asserted") return false;

  var intent = workObject(value.intent);
  if (!hasKeys(intent, ["summary", "acceptance_criteria", "constraints"])) return false;
  if (!hasOnlyKeys(intent, ["summary", "objective", "acceptance_criteria", "constraints", "request_ref"])) return false;
  if (intent.request_ref !== undefined && !isExactWorkRefShape(intent.request_ref)) return false;

  var authority = workObject(value.authority);
  if (!hasKeys(authority, ["mode", "status", "scope", "constraints"])) return false;
  if (!hasOnlyKeys(authority, ["mode", "status", "scope", "authorization_ref", "constraints", "valid_from", "valid_until"])) return false;
  if (authority.authorization_ref !== undefined && !isExactWorkRefShape(authority.authorization_ref)) return false;
  var scope = workObject(authority.scope);
  if (!hasKeys(scope, ["actions", "resources", "systems"])) return false;
  if (!hasOnlyKeys(scope, ["actions", "resources", "systems", "spend_limit"])) return false;
  if (!isExactWorkRefListShape(scope.resources)) return false;
  if (scope.spend_limit !== undefined) {
    if (!hasKeys(scope.spend_limit, ["currency", "amount"]) || !hasOnlyKeys(scope.spend_limit, ["currency", "amount"])) return false;
  }

  var cost = workObject(value.cost);
  if (!cost) return false;
  if (cost.availability === "not_observed") {
    if (!hasKeys(cost, ["availability"]) || !hasOnlyKeys(cost, ["availability"])) return false;
  } else {
    if (!hasKeys(cost, ["availability", "currency", "total", "estimated"])) return false;
    if (!hasOnlyKeys(cost, ["availability", "currency", "total", "estimated", "source"])) return false;
  }

  if (!isExactWorkRefListShape(value.artifact_refs) || !isExactWorkRefListShape(value.evidence_refs)) return false;
  if (value.semantic_refs !== undefined) {
    var semantic = workObject(value.semantic_refs);
    var semanticLists = ["decision_refs", "risk_refs", "constraint_refs", "expectation_refs", "learning_refs", "prior_receipt_refs"];
    if (!hasKeys(semantic, semanticLists)) return false;
    if (!hasOnlyKeys(semantic, semanticLists.concat(["context_capsule_ref", "context_delivered_at"]))) return false;
    for (var i = 0; i < semanticLists.length; i++) {
      if (!isExactWorkRefListShape(semantic[semanticLists[i]])) return false;
    }
    if (semantic.context_capsule_ref !== undefined && !isExactWorkRefShape(semantic.context_capsule_ref)) return false;
  }
  return true;
}

${buildCredentialFreeHttpsUriHookFragment()}

function workStrings(value, max) {
  if (value === undefined) return [];
  if (!Array.isArray(value) || value.length > 20) return null;
  var output = [];
  for (var i = 0; i < value.length; i++) {
    var item = workString(value[i], max);
    if (!item) return null;
    if (output.indexOf(item) < 0) output.push(item);
  }
  return output;
}

function workRef(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  var system = workString(value.system, 120);
  var type = workString(value.type, 120);
  var id = workString(value.id, 500);
  var uri = value.uri === undefined ? undefined : workString(value.uri, 1000);
  var version = value.version === undefined ? undefined : workString(value.version, 120);
  if (!system || !type || !id || uri === null || version === null) return null;
  if (uri && !isCredentialFreeHttpsUri(uri)) return null;
  var ref = { system: system, type: type, id: id };
  if (uri) ref.uri = uri;
  if (version) ref.version = version;
  return ref;
}

function workRefs(value) {
  if (value === undefined) return [];
  if (!Array.isArray(value) || value.length > 20) return null;
  var refs = [];
  var seen = {};
  for (var i = 0; i < value.length; i++) {
    var ref = workRef(value[i]);
    if (!ref) return null;
    var key = ref.system + "\u0000" + ref.type + "\u0000" + ref.id;
    if (!seen[key]) {
      seen[key] = true;
      refs.push(ref);
    }
  }
  refs.sort(function (a, b) {
    var left = a.system + "\u0000" + a.type + "\u0000" + a.id;
    var right = b.system + "\u0000" + b.type + "\u0000" + b.id;
    return left < right ? -1 : left > right ? 1 : 0;
  });
  return refs;
}

function workIso(value) {
  var text = workString(value, 100);
  return text && /^\\d{4}-\\d{2}-\\d{2}T\\d{2}:\\d{2}:\\d{2}(?:\\.\\d+)?(?:Z|[+-]\\d{2}:\\d{2})$/.test(text) && !Number.isNaN(Date.parse(text))
    ? text
    : null;
}

export function parseExplicitWorkContext(value) {
  if (!value || Buffer.byteLength(String(value), "utf8") > 4096) return null;
  var parsed = parseJson(value);
  if (parsed.schema_version !== "orgx-session-work-context/v1") return null;
  var intent = workObject(parsed.intent);
  var authority = workObject(parsed.authority);
  var scope = workObject(authority && authority.scope);
  var cost = workObject(parsed.cost);
  if (!intent || !authority || !scope || !cost) return null;
  var summary = workString(intent.summary, 2000);
  var objective = intent.objective === undefined ? undefined : workString(intent.objective, 4000);
  var acceptance = workStrings(intent.acceptance_criteria, 1000);
  var intentConstraints = workStrings(intent.constraints, 1000);
  var requestRef = intent.request_ref === undefined ? undefined : workRef(intent.request_ref);
  var modes = ["explicit", "delegated", "inherited", "policy", "none", "unknown"];
  var statuses = ["granted", "restricted", "denied", "expired", "unknown"];
  var mode = workString(authority.mode, 40);
  var status = workString(authority.status, 40);
  var actions = workStrings(scope.actions, 120);
  var resources = workRefs(scope.resources);
  var systems = workStrings(scope.systems, 120);
  var spend = scope.spend_limit === undefined ? undefined : workObject(scope.spend_limit);
  var spendCurrency = spend === undefined ? undefined : workString(spend && spend.currency, 12);
  var spendAmount = spend && spend.amount;
  var authorityConstraints = workStrings(authority.constraints, 1000);
  var authorizationRef = authority.authorization_ref === undefined ? undefined : workRef(authority.authorization_ref);
  var validFrom = authority.valid_from === undefined ? undefined : workIso(authority.valid_from);
  var validUntil = authority.valid_until === undefined ? undefined : workIso(authority.valid_until);
  var artifactRefs = workRefs(parsed.artifact_refs);
  var evidenceRefs = workRefs(parsed.evidence_refs);
  var semantic = parsed.semantic_refs === undefined ? undefined : workObject(parsed.semantic_refs);
  var contextCapsuleRef = !semantic || semantic.context_capsule_ref === undefined
    ? undefined
    : workRef(semantic.context_capsule_ref);
  var contextDeliveredAt = !semantic || semantic.context_delivered_at === undefined
    ? undefined
    : workIso(semantic.context_delivered_at);
  var decisionRefs = !semantic ? [] : workRefs(semantic.decision_refs);
  var riskRefs = !semantic ? [] : workRefs(semantic.risk_refs);
  var constraintRefs = !semantic ? [] : workRefs(semantic.constraint_refs);
  var expectationRefs = !semantic ? [] : workRefs(semantic.expectation_refs);
  var learningRefs = !semantic ? [] : workRefs(semantic.learning_refs);
  var priorReceiptRefs = !semantic ? [] : workRefs(semantic.prior_receipt_refs);
  var costNotObserved = cost.availability === "not_observed";
  var costKeys = Object.keys(cost);
  var currency = costNotObserved ? undefined : workString(cost.currency, 12);
  var source = costNotObserved || cost.source === undefined ? undefined : workString(cost.source, 120);
  if (!summary || objective === null || acceptance === null || intentConstraints === null || requestRef === null) return null;
  if (!mode || modes.indexOf(mode) < 0 || !status || statuses.indexOf(status) < 0) return null;
  if (actions === null || resources === null || systems === null || authorityConstraints === null || authorizationRef === null) return null;
  if (spend !== undefined && (!spend || !spendCurrency || !/^[A-Z][A-Z0-9_-]{1,11}$/.test(spendCurrency) || typeof spendAmount !== "number" || !Number.isFinite(spendAmount) || spendAmount < 0 || spendAmount > 100000)) return null;
  if (validFrom === null || validUntil === null || (validFrom && validUntil && Date.parse(validUntil) < Date.parse(validFrom))) return null;
  if (costNotObserved) {
    if (costKeys.length !== 1) return null;
  } else {
    if (cost.availability !== undefined && cost.availability !== "available") return null;
    if (!currency || !/^[A-Z][A-Z0-9_-]{1,11}$/.test(currency) || typeof cost.total !== "number" || !Number.isFinite(cost.total) || cost.total < 0 || cost.total > 100000 || source === null) return null;
    if (cost.estimated !== undefined && typeof cost.estimated !== "boolean") return null;
  }
  if (artifactRefs === null || evidenceRefs === null) return null;
  if (parsed.semantic_refs !== undefined && !semantic) return null;
  if (contextCapsuleRef === null || contextDeliveredAt === null || decisionRefs === null || riskRefs === null || constraintRefs === null || expectationRefs === null || learningRefs === null || priorReceiptRefs === null) return null;
  if (Boolean(contextCapsuleRef) !== Boolean(contextDeliveredAt)) return null;
  var semanticRefCount = decisionRefs.length + riskRefs.length + constraintRefs.length + expectationRefs.length + learningRefs.length + priorReceiptRefs.length + (contextCapsuleRef ? 1 : 0);
  if (semantic !== undefined && (semanticRefCount === 0 || semanticRefCount > 20)) return null;
  var normalizedIntent = { summary: summary, acceptance_criteria: acceptance, constraints: intentConstraints };
  if (objective) normalizedIntent.objective = objective;
  if (requestRef) normalizedIntent.request_ref = requestRef;
  var normalizedAuthority = {
    mode: mode,
    status: status,
    scope: { actions: actions, resources: resources, systems: systems },
    constraints: authorityConstraints,
  };
  if (spend !== undefined) normalizedAuthority.scope.spend_limit = { currency: spendCurrency, amount: spendAmount };
  if (authorizationRef) normalizedAuthority.authorization_ref = authorizationRef;
  if (validFrom) normalizedAuthority.valid_from = validFrom;
  if (validUntil) normalizedAuthority.valid_until = validUntil;
  var normalizedCost = costNotObserved
    ? { availability: "not_observed" }
    : { availability: "available", currency: currency, total: cost.total, estimated: cost.estimated !== false };
  if (!costNotObserved && source) normalizedCost.source = source;
  var normalizedSemantic;
  if (semantic !== undefined) {
    normalizedSemantic = {
      decision_refs: decisionRefs,
      risk_refs: riskRefs,
      constraint_refs: constraintRefs,
      expectation_refs: expectationRefs,
      learning_refs: learningRefs,
      prior_receipt_refs: priorReceiptRefs,
    };
    if (contextCapsuleRef) {
      normalizedSemantic.context_capsule_ref = contextCapsuleRef;
      normalizedSemantic.context_delivered_at = contextDeliveredAt;
    }
  }
  return {
    schema_version: "orgx-session-work-context/v1",
    provenance: "producer_asserted",
    intent: normalizedIntent,
    authority: normalizedAuthority,
    cost: normalizedCost,
    artifact_refs: artifactRefs,
    evidence_refs: evidenceRefs,
    ...(normalizedSemantic ? { semantic_refs: normalizedSemantic } : {}),
  };
}

function canonicalWorkJson(value) {
  if (Array.isArray(value)) {
    return "[" + value.map(canonicalWorkJson).join(",") + "]";
  }
  if (value && typeof value === "object") {
    return "{" + Object.keys(value)
      .filter(function (key) { return value[key] !== undefined; })
      .sort()
      .map(function (key) {
        return JSON.stringify(key) + ":" + canonicalWorkJson(value[key]);
      })
      .join(",") + "}";
  }
  return JSON.stringify(value);
}

function workContextSha256(value) {
  return createHash("sha256").update(canonicalWorkJson(value)).digest("hex");
}

function readWorkContextActivation(
  path,
  cwd,
  nowIso,
  sourceClient,
  sessionId,
  allowExpired
) {
  if (!path || !cwd) return null;
  try {
    var raw = readFileSync(path, "utf8");
    if (Buffer.byteLength(raw, "utf8") > 16384) return null;
    var activation = parseJson(raw);
    var scoped = activation.schema_version === "orgx-session-work-context-activation/v2";
    if (!scoped && activation.schema_version !== "orgx-session-work-context-activation/v1") return null;
    if (scoped) {
      if (!hasKeys(activation, ["schema_version", "created_at", "valid_until", "cwd", "source_client", "session_id", "context_sha256", "context"])) return null;
      if (!hasOnlyKeys(activation, ["schema_version", "created_at", "valid_until", "cwd", "source_client", "session_id", "context_sha256", "context"])) return null;
      if (!isExactWorkContextShape(activation.context)) return null;
      if (!validNativeSourceClient(activation.source_client)) return null;
      if (!validNativeSessionId(activation.session_id)) return null;
      if (typeof activation.context_sha256 !== "string" || !/^[0-9a-f]{64}$/.test(activation.context_sha256)) return null;
    }
    var activeCwd = workString(activation.cwd, 4000);
    var createdAt = workIso(activation.created_at);
    var validUntil = workIso(activation.valid_until);
    if (!activeCwd || !createdAt || !validUntil) return null;
    if (Date.parse(validUntil) < Date.parse(createdAt)) return null;
    if (!allowExpired && Date.parse(validUntil) <= Date.parse(nowIso)) return null;
    if (canonicalWorkCwd(activeCwd) !== canonicalWorkCwd(cwd)) return null;
    if (scoped) {
      if (!sourceClient || !sessionId) return null;
      if (activation.source_client !== sourceClient || activation.session_id !== sessionId) return null;
    } else if (sourceClient || sessionId) {
      return null;
    }
    if (scoped && activation.context_sha256 !== workContextSha256(activation.context)) return null;
    var context = parseExplicitWorkContext(JSON.stringify(activation.context));
    if (!context) return null;
    return { activation: activation, context: context };
  } catch (e) {
    return null;
  }
}

export function readActivatedWorkContext(path, cwd, nowIso, sourceClient, sessionId) {
  var record = readWorkContextActivation(path, cwd, nowIso, sourceClient, sessionId);
  return record ? record.context : null;
}

function scopedWorkContextActivation(
  cwd,
  sourceClient,
  sessionId,
  activation,
  context
) {
  return {
    schema_version: "orgx-session-work-context-activation/v2",
    created_at: activation.created_at,
    valid_until: activation.valid_until,
    cwd: canonicalWorkCwd(cwd),
    source_client: sourceClient,
    session_id: sessionId,
    context_sha256: workContextSha256(context),
    context: context,
  };
}

function claimLegacyActivatedWorkContext(env, overridePath, cwd, nowIso, sourceClient, sessionId) {
  if (
    !cwd ||
    !claimableNativeSourceClient(sourceClient) ||
    !validNativeSessionId(sessionId)
  ) return null;
  var genericPath = overridePath || defaultWorkContextPath(env, cwd);
  var exactPath = defaultWorkContextPath(env, cwd, sourceClient, sessionId);
  if (!genericPath || !exactPath) return null;

  var claimed = withStateLock(genericPath, function () {
    var alreadyBound = readWorkContextActivation(
      genericPath,
      cwd,
      nowIso,
      sourceClient,
      sessionId
    );
    if (alreadyBound && alreadyBound.activation.schema_version === "orgx-session-work-context-activation/v2") {
      return alreadyBound.context;
    }
    var legacy = readWorkContextActivation(genericPath, cwd, nowIso);
    if (!legacy || legacy.activation.schema_version !== "orgx-session-work-context-activation/v1") {
      return null;
    }
    var scoped = scopedWorkContextActivation(
      cwd,
      sourceClient,
      sessionId,
      legacy.activation,
      legacy.context
    );

    // First rewrite the generic lease as a scoped lease. If this process dies
    // before the exact-path copy is durable, no second session can consume it.
    if (!writeState(genericPath, scoped)) return null;
    if (overridePath) return scoped.context;
    if (!writeState(exactPath, scoped)) return scoped.context;
    removeState(genericPath);
    return scoped.context;
  });
  if (claimed.acquired && claimed.value) return claimed.value;

  var raced = readWorkContextActivation(
    exactPath,
    cwd,
    nowIso,
    sourceClient,
    sessionId
  ) || readWorkContextActivation(
    genericPath,
    cwd,
    nowIso,
    sourceClient,
    sessionId
  );
  return raced ? raced.context : null;
}

function readDefaultActivatedWorkContext(
  env,
  overridePath,
  cwd,
  nowIso,
  sourceClient,
  sessionId,
  claimLegacy
) {
  if (overridePath) {
    return readActivatedWorkContext(overridePath, cwd, nowIso, sourceClient, sessionId)
      || (claimLegacy
        ? claimLegacyActivatedWorkContext(
            env,
            overridePath,
            cwd,
            nowIso,
            sourceClient,
            sessionId
          )
        : null);
  }
  var scoped = readActivatedWorkContext(
    defaultWorkContextPath(env, cwd, sourceClient, sessionId),
    cwd,
    nowIso,
    sourceClient,
    sessionId
  );
  if (scoped) return scoped;
  var genericPath = defaultWorkContextPath(env, cwd);
  var recovered = readActivatedWorkContext(
    genericPath,
    cwd,
    nowIso,
    sourceClient,
    sessionId
  );
  if (recovered) return recovered;
  return claimLegacy
    ? claimLegacyActivatedWorkContext(
        env,
        null,
        cwd,
        nowIso,
        sourceClient,
        sessionId
      )
    : null;
}

function clearConsumedActivatedWorkContext(
  env,
  overridePath,
  cwd,
  nowIso,
  sourceClient,
  sessionId,
  context
) {
  if (!cwd || !sourceClient || !sessionId || !context) return false;
  var candidates = [];
  var exactPath = defaultWorkContextPath(env, cwd, sourceClient, sessionId);
  var genericPath = defaultWorkContextPath(env, cwd);
  for (var candidate of [overridePath, exactPath, genericPath]) {
    if (candidate && candidates.indexOf(candidate) < 0) candidates.push(candidate);
  }
  var expectedSha256 = workContextSha256(context);
  var cleared = false;
  for (var candidatePath of candidates) {
    var record = readWorkContextActivation(
      candidatePath,
      cwd,
      nowIso,
      sourceClient,
      sessionId,
      true
    );
    if (
      !record ||
      record.activation.schema_version !== "orgx-session-work-context-activation/v2" ||
      workContextSha256(record.context) !== expectedSha256
    ) {
      continue;
    }
    if (removeState(candidatePath)) cleared = true;
  }
  return cleared;
}

function autoFlushDisabled(value) {
  var normalized = String(value || "").trim().toLowerCase();
  return normalized === "off" || normalized === "false" || normalized === "0";
}

/**
 * Ask a detached Wizard process to deliver the durable queue. The hook itself
 * performs no network or credential work and never waits for delivery.
 */
export function triggerQueueDelivery(args, env, queueDir, spawnImpl) {
  if (autoFlushDisabled(env.ORGX_SESSION_SUMMARY_AUTO_FLUSH)) return false;
  var nodePath = pickString(args.delivery_node);
  var cliPath = pickString(args.delivery_cli);
  if (!nodePath || !cliPath || !queueDir) return false;
  var configuredLimit = parseInt(
    pickString(args.auto_flush_limit, env.ORGX_SESSION_SUMMARY_AUTO_FLUSH_LIMIT) || "",
    10
  );
  var limit = Number.isFinite(configuredLimit)
    ? Math.max(1, Math.min(configuredLimit, 100))
    : 25;
  try {
    var child = spawnImpl(
      nodePath,
      [
        cliPath,
        "hooks",
        "flush",
        "--background",
        "--limit=" + String(limit),
        "--queue=" + queueDir,
      ],
      { detached: true, stdio: "ignore" }
    );
    if (child && typeof child.on === "function") {
      child.on("error", function () {});
    }
    if (child && typeof child.unref === "function") child.unref();
    return true;
  } catch (e) {
    return false;
  }
}

export async function main(options) {
  var opts = options || {};
  var argv = opts.argv ? opts.argv : process.argv.slice(2);
  var env = opts.env ? opts.env : process.env;
  var stdinText = opts.stdinText === undefined ? "" : opts.stdinText;
  var now = opts.now ? opts.now : function () { return new Date().toISOString(); };
  var dir = opts.dir;
  var spawnImpl = opts.spawnImpl ? opts.spawnImpl : spawn;

  var args = parseArgs(argv);
  var payload = parseJson(stdinText);
  var nowIso = now();
  var environmentWorkContext = parseExplicitWorkContext(env.ORGX_SESSION_WORK_CONTEXT);
  var workContextPathOverride = pickString(
    env.ORGX_SESSION_WORK_CONTEXT_PATH,
    args.work_context_path
  );

  var event = pickString(args.event, payload.hook_event_name, payload.hookEventName, "unknown");
  var sessionId = pickString(
    payload.session_id,
    payload.sessionId,
    payload.conversation_id,
    payload.conversationId,
    payload.thread_id,
    payload.threadId,
    args.session_id
  );
  if (!sessionId) return { ok: true, skipped: "missing_session_id" };

  var sourceClient = pickString(args.source_client, env.ORGX_SOURCE_CLIENT, "claude-code");
  var cwd = pickString(payload.cwd, args.cwd);
  var stateDir = dir ? dir : pickString(args.state_dir, env.ORGX_SESSION_STATE_DIR);
  var path = statePath(sessionId, sourceClient, stateDir);
  var oldPath = legacyStatePath(sessionId, stateDir);

  var terminal = event === "Stop" || event === "RunEnd" || event === "SessionEnd";

  if (!terminal) {
    // The lock covers the read, both logical projections, and the atomic
    // rename. Concurrent hook processes therefore cannot lose increments.
    var hotUpdate = withStateLock(path, function () {
      var loaded = readStateWithLegacy(
        path,
        oldPath,
        sessionId,
        sourceClient
      );
      var envelope = loaded
        ? loaded.envelope
        : emptyStateEnvelope(sessionId, sourceClient, cwd, nowIso);
      var conversation = envelope.conversation;
      var run = ensureRunState(envelope, cwd || conversation.cwd, nowIso);
      var states = [conversation, run];
      for (var stateIndex = 0; stateIndex < states.length; stateIndex++) {
        if (!states[stateIndex].cwd && cwd) {
          states[stateIndex].cwd = cwd;
          states[stateIndex].repo = repoOf(cwd);
        }
      }
      var activeCwd = cwd || conversation.cwd || run.cwd;
      if (!conversation.work_context && conversation.work_context_locked !== true) {
        var activeWorkContext = environmentWorkContext || readDefaultActivatedWorkContext(
          env,
          workContextPathOverride,
          activeCwd,
          nowIso,
          sourceClient,
          sessionId,
          event === "SessionStart"
        );
        if (activeWorkContext) conversation.work_context = activeWorkContext;
      }
      if (!run.work_context && conversation.work_context) {
        run.work_context = conversation.work_context;
      }
      var toolName = pickString(
        payload.tool_name,
        payload.toolName,
        payload.tool ? payload.tool.name : undefined
      );
      var actionDescriptor = safeActionDescriptor(toolName, payload, activeCwd);
      var observation = {
        event: event,
        toolName: toolName,
        toolUseId: pickString(payload.tool_use_id, payload.toolUseId),
        turnId: pickString(payload.turn_id, payload.turnId),
        durationMs: payload.duration_ms,
        permissionMode: pickString(payload.permission_mode, payload.permissionMode),
        actionEffect: actionDescriptor.effect,
        actionTarget: actionDescriptor.target,
        promptText: pickString(payload.prompt, payload.user_prompt, payload.message, payload.text),
        captureWorkEpisode: workEpisodeCaptureEnabled(
          pickString(args.work_episode_capture, env.ORGX_SESSION_WORK_EPISODE_CAPTURE)
        ),
        parentSessionId: pickString(payload.parent_session_id, payload.parentSessionId),
        rootSessionId: pickString(payload.root_session_id, payload.rootSessionId),
        resumedFromSessionId: pickString(payload.resumed_from_session_id, payload.resumedFromSessionId),
        nowIso: nowIso,
      };
      applyEvent(conversation, observation);
      applyEvent(run, observation);
      if (isPromptEvent(event) || toolName) {
        conversation.work_context_locked = true;
        run.work_context_locked = true;
      }
      if (!writeState(path, envelope)) {
        return { ok: true, skipped: "state_write_failed" };
      }
      if (loaded && loaded.legacy_path) removeState(loaded.legacy_path);
      return {
        ok: true,
        counted: true,
        event: event,
        session_start_context: event === "SessionStart"
          ? conversation.work_context
          : null,
      };
    });
    if (!hotUpdate.acquired) return { ok: true, skipped: "state_lock_timeout" };
    var hotResult = hotUpdate.value;
    if (
      hotResult &&
      event === "SessionStart" &&
      (sourceClient === "claude-code" || sourceClient === "codex") &&
      hotResult.session_start_context
    ) {
      hotResult.hook_output = buildNativeSessionStartHookOutput(
        hotResult.session_start_context
      );
    }
    if (hotResult) delete hotResult.session_start_context;
    return hotResult;
  }

  // Terminal capture and state transition share the same lock. RunEnd resets
  // only the run projection; the cumulative conversation survives until an
  // acknowledged local SessionEnd queue write.
  var initiativeId = pickString(env.ORGX_INITIATIVE_ID, args.initiative);
  var queueDir = pickString(args.queue_dir, env.ORGX_SESSION_SUMMARY_QUEUE_DIR) || defaultQueueDir();
  var captureKind = event === "SessionEnd"
    ? "session_end"
    : event === "RunEnd"
    ? "run_end"
    : "turn_boundary";
  var terminalUpdate = withStateLock(path, function () {
    var loaded = readStateWithLegacy(
      path,
      oldPath,
      sessionId,
      sourceClient
    );
    if (!loaded) return { ok: true, skipped: "no_session_state" };
    var envelope = loaded.envelope;
    var selected = event === "RunEnd" ? envelope.run : envelope.conversation;
    if (!selected) return { ok: true, skipped: "no_run_state" };
    var summary = buildSummary(selected, nowIso, captureKind);
    var queued = enqueueSummaryCapture(queueDir, summary, initiativeId, nowIso, captureKind);
    if (!queued) return { ok: true, skipped: "queue_write_failed", summary: summary };

    var statePersisted = true;
    if (event === "RunEnd") {
      envelope.run = null;
      statePersisted = writeState(path, envelope);
    } else if (event === "SessionEnd") {
      statePersisted = removeState(path);
    } else {
      statePersisted = writeState(path, envelope);
    }
    if (statePersisted && loaded.legacy_path) {
      statePersisted = removeState(loaded.legacy_path);
    }
    var activationCleared = false;
    if (statePersisted && event === "SessionEnd") {
      activationCleared = clearConsumedActivatedWorkContext(
        env,
        workContextPathOverride,
        selected.cwd || cwd,
        nowIso,
        sourceClient,
        sessionId,
        selected.work_context
      );
    }

    return {
      ok: true,
      queued: true,
      queue_path: queued.path,
      final: event === "SessionEnd",
      state_persisted: statePersisted,
      activation_cleared: activationCleared,
      summary: queued.capture.session,
    };
  });
  if (!terminalUpdate.acquired) return { ok: true, skipped: "state_lock_timeout" };
  var terminalResult = terminalUpdate.value;
  if (!terminalResult || !terminalResult.queued) return terminalResult;
  terminalResult.delivery_triggered = triggerQueueDelivery(args, env, queueDir, spawnImpl);
  return terminalResult;
}

var invokedDirectly = false;
try {
  invokedDirectly = Boolean(process.argv[1]) &&
    realpathSync(fileURLToPath(import.meta.url)) === realpathSync(process.argv[1]);
} catch (e) {}
if (invokedDirectly) {
  readStdin()
    .then(function (text) { return main({ stdinText: text }); })
    .then(function (result) {
      if (result && result.hook_output) {
        process.stdout.write(JSON.stringify(result.hook_output) + String.fromCharCode(10));
      }
    })
    .catch(function () {})
    .finally(function () { process.exit(0); });
}
`;
}
